import format from 'date-fns/format';
import { formatInTimeZone } from 'date-fns-tz';
import { WidgetActionType } from 'Pages/Widget/Widget.store';
import { useContext, useMemo } from 'react';
import { RecurrenceType } from 'Pages/Widget/Widget.types';
import { compareDates } from 'shared/compareDates';
import { getTime, startOfDay, parseISO } from 'date-fns';
import { getDifferenceInWeeksFromRequestedToCurrentDate } from 'shared/getDifferenceInWeeksFromRequestedToCurrentDate';
import { isAppointmentTypeOneTime } from 'shared/recurring';
import { WidgetContext } from 'Pages/Widget/Widget.constants';
import { CustomCalendarDayProps } from './CustomCalendarDay.types';
import * as S from './CustomCalendarDay.styles';

const CustomCalendarDay = ({ date, withResultDays, onDayClickCallback, buildingTimezone }: CustomCalendarDayProps) => {
  const { dispatch, state } = useContext(WidgetContext);
  const { selectedAppointmentType, selectedRecurrenceType, selectedRequestedDate, selectedEndDate } = state;

  const parsedDate = new Date(date);
  const day = format(parsedDate, 'd');

  const currentCalendarDayWithTimezone = parseISO(
    formatInTimeZone(new Date(), buildingTimezone, 'yyyy-MM-dd HH:mm:ss'),
  );
  const isPast = getTime(startOfDay(currentCalendarDayWithTimezone)) <= getTime(date);

  const isOneTime = isAppointmentTypeOneTime(selectedAppointmentType);
  
  // Determine if this date is the start date, end date, or in range
  const isStartDate = selectedRequestedDate ? compareDates(selectedRequestedDate, parsedDate) === 0 : false;
  const isEndDate = selectedEndDate && !isOneTime ? compareDates(selectedEndDate, parsedDate) === 0 : false;
  const isInRange = useMemo(() => {
    // For one-time bookings, don't show range highlighting
    if (isOneTime || !selectedRequestedDate || !selectedEndDate || isStartDate || isEndDate) {
      return false;
    }
    const startComparison = compareDates(parsedDate, selectedRequestedDate);
    const endComparison = compareDates(parsedDate, selectedEndDate);
    return startComparison === -1 && endComparison === 1;
  }, [selectedRequestedDate, selectedEndDate, parsedDate, isStartDate, isEndDate, isOneTime]);

  const handlePickDay = () => {
    if (isPast && dispatch) {
      const isOneTime = isAppointmentTypeOneTime(selectedAppointmentType);
      
      if (isOneTime) {
        // For one-time bookings: only allow single date selection
        // Always replace the selected date when clicking a new date
        dispatch({
          type: WidgetActionType.UPDATE_REQUESTED_DATE,
          payload: { requestedDate: parsedDate },
        });
        // Clear end date if it exists (shouldn't happen, but just in case)
        if (selectedEndDate) {
          dispatch({
            type: WidgetActionType.UPDATE_END_DATE,
            payload: { endDate: undefined },
          });
        }
      } else if (!selectedRequestedDate) {
        // For recurring bookings: allow date range selection
        // First click: set start date
        dispatch({
          type: WidgetActionType.UPDATE_REQUESTED_DATE,
          payload: { requestedDate: parsedDate },
        });
      } else if (!selectedEndDate) {
          // Second click: set end date or reset start date
          const comparison = compareDates(selectedRequestedDate, parsedDate);
          if (comparison === 1) {
            // Clicked date is after start date, set as end date
            dispatch({
              type: WidgetActionType.UPDATE_END_DATE,
              payload: { endDate: parsedDate },
            });
          } else {
            // Clicked date is before or equal to start date, reset start date
            dispatch({
              type: WidgetActionType.UPDATE_REQUESTED_DATE,
              payload: { requestedDate: parsedDate },
            });
            dispatch({
              type: WidgetActionType.UPDATE_END_DATE,
              payload: { endDate: undefined },
            });
          }
        } else {
          // Both dates selected: reset and set new start date
          dispatch({
            type: WidgetActionType.UPDATE_REQUESTED_DATE,
            payload: { requestedDate: parsedDate },
          });
          dispatch({
            type: WidgetActionType.UPDATE_END_DATE,
            payload: { endDate: undefined },
          });
        }
      if (onDayClickCallback) {
        onDayClickCallback();
      }
    }
  };

  const isRecurringDate = useMemo(() => {
    if (!selectedRequestedDate) {
      return false;
    }

    if (isAppointmentTypeOneTime(selectedAppointmentType)) {
      return false;
    }

    if (!selectedEndDate) {
      return false;
    }

    const currentDate = new Date(date);

    // Check if date is within the selected range
    const startComparison = compareDates(currentDate, selectedRequestedDate);
    const endComparison = compareDates(currentDate, selectedEndDate);
    // Date must be >= start date and <= end date
    const isWithinRange = startComparison <= 0 && endComparison >= 0;

    // Only highlight recurring dates that are within the selected range
    if (!isWithinRange) {
      return false;
    }

    const { isCurrentDayMatchesNextRecurringWeekStartDay, diffInWeeks } =
      getDifferenceInWeeksFromRequestedToCurrentDate(currentDate, selectedRequestedDate);

    const isCurrentDateAfterSelectedDay = compareDates(selectedRequestedDate, currentDate) === 1;

    const isDateEvery2Weeks =
      diffInWeeks % 2 === 0 &&
      isCurrentDayMatchesNextRecurringWeekStartDay &&
      isCurrentDateAfterSelectedDay &&
      isWithinRange;

    const isDateEvery3Weeks =
      diffInWeeks >= 3 &&
      diffInWeeks % 3 === 0 &&
      isCurrentDayMatchesNextRecurringWeekStartDay &&
      isCurrentDateAfterSelectedDay &&
      isWithinRange;

    const isDateEvery4Weeks =
      diffInWeeks >= 4 &&
      diffInWeeks % 4 === 0 &&
      isCurrentDayMatchesNextRecurringWeekStartDay &&
      isCurrentDateAfterSelectedDay &&
      isWithinRange;

    return {
      [RecurrenceType.Weekly]:
        isCurrentDayMatchesNextRecurringWeekStartDay && isCurrentDateAfterSelectedDay && isWithinRange,
      [RecurrenceType.Every2Weeks]: isDateEvery2Weeks,
      [RecurrenceType.Every3Weeks]: isDateEvery3Weeks,
      [RecurrenceType.Every4Weeks]: isDateEvery4Weeks,
      [RecurrenceType.Daily]: false,
    }[selectedRecurrenceType ?? RecurrenceType.Daily];
  }, [selectedRequestedDate, selectedAppointmentType, selectedEndDate, date, selectedRecurrenceType]);

  const isSelected = isStartDate || (isEndDate && isRecurringDate);

  // Check if date is in range but not a recurring date (for subtle highlighting)
  // Include end date if it's not a recurring date
  // For one-time bookings, don't show range highlighting
  const isInRangeButNotRecurring = useMemo(() => {
    if (isOneTime) {
      return false;
    }
    if (isEndDate && !isRecurringDate) {
      return true;
    }
    return isInRange && !isRecurringDate && !isStartDate && !isEndDate;
  }, [isInRange, isRecurringDate, isStartDate, isEndDate, isOneTime]);

  return (
    <S.DayWrapper isPast={!isPast} onClick={handlePickDay}>
      <S.MonthDay
        isOneTime={isOneTime}
        withResultDays={withResultDays}
        isSelected={isSelected}
        isStartDate={isStartDate}
        isEndDate={isEndDate}
        isInRange={isInRangeButNotRecurring}
        isRecurringDate={isRecurringDate}
      >
        {day}
      </S.MonthDay>
    </S.DayWrapper>
  );
};

export default CustomCalendarDay;
